import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { HelmetProvider, Helmet } from 'react-helmet-async';

import { ClerkProvider } from '@clerk/react'; // Import ClerkProvider

import HomePage from "./pages/home.jsx";
import PricingPage from "./pages/pricing.jsx";
import PricingPlansPage from "./pages/pricingPlansPage.jsx";
import BookCallPage from "./pages/bookCallPage.jsx";
import PartnerPage from "./pages/partners/partners.jsx";
import PrivacyPolicyPage from "./pages/privacypolicy.jsx";
import TermsPage from "./pages/terms.jsx";
import CaseStudy from "./pages/caseStudy";
import NotFoundPage from "./pages/notFoundPage.jsx";

import "./styles/Pablooflow.css";
import "./styles/pablooflow-overrides.css";
import "./styles/tailwind.css";
import "./styles/main.css";
import "./styles/hero-pablooprod/pablooprodFont.css";
import "./styles/hero-pablooprod/pablooprodStyle.css";

import reportWebVitals from "./reportWebVitals";
import TermsComponent from "./components/TermsComponent.js";
import Features from "./pages/features";
import ScheduleCall from "./pages/scheduleCall";
import {sendSlackMessage} from "./models/notifications";
import ForwardingPage from "./pages/ForwardingPage";
import StoreCreditApi from "./pages/storeCreditApi";
import PartnersJoin from "./pages/partners/join";
import Cart from "./pages/checkout/cart";
import CheckoutPage from "./pages/checkout/checkoutPage";
import OrderStatusPage from "./pages/checkout/orderStatusPage";
import PaymentPage from "./pages/checkout/paymentPage";
import CashbackProductPage from "./pages/products/cashbackProductPage";
import CashbackSolutionsPage from "./pages/solutions/cashbackSolutionsPage";
import MembershipsProductPage from "./pages/products/membershipsProductPage";
import StoreCreditRefundsProductPage from "./pages/products/storeCreditRefundsProductPage";
import CashbackInfoPage from "./pages/solutions/cashbackInfo.jsx";
import CartAbandonmentPage from "./pages/solutions/cartAbandonment.jsx";
import CashbackVsDiscountsPage from "./pages/solutions/cashbackVsDiscounts.jsx";
import CashbackSuccessPage from "./pages/solutions/cashbackSuccess.jsx";
import MainSolutionsPage from "./pages/solutions/mainSolutionsPage";
import MainProductPage from "./pages/products/mainProductPage";
import NoChargebacksComponent from "./components/solutions/quickPages/NoChargebacksComponent";
import InventoryLiquidation from "./components/solutions/quickPages/inventoryLiquidation";
import BlogsHubPage from "./pages/blogsHub/blogsHubPage";
import UsersUsersUsersPage from "./pages/solutions/usersUsersUsers";
import GuidedEconomy from "./pages/GuidedEconomy";
import ManagedPage from "./pages/managed.jsx";

// Google Analytics
window.dataLayer = window.dataLayer || [];
window.gtag = function () {
  window.dataLayer.push(arguments);
};
window.gtag("js", new Date());
window.gtag("config", "G-RY13LC0YDT");

// Clerk Account Management
const publishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;


const getPageTitle = (pathname) => {
  switch (pathname) {
    case "/":
      return "Pabloo Home";
    case "/pricing":
      return "Pabloo Pricing";
    case "/plans":
      return "Pabloo Plans & Pricing";
    case "/partners":
      return "Pabloo Partnerships";
    case "/partners/join":
      return "Pabloo Join Partnerships";
    case "/store-credit-api":
      return "Store Credit API Pabloo";
    case "/privacy-policy":
      return "Pabloo Privacy Policy";
    case "/terms-and-conditions":
      return "Pabloo Terms and Conditions";
    case "/case-study":
      return "Pabloo Case Study";
    case "/case-study/parks-project-study":
      return "Pabloo Parks Project CaseStudy";
    case "/case-study/balodana-study":
      return "Pabloo Balodana CaseStudy";
    case "/case-study/planted-material-study":
      return "Pabloo Planted Materials CaseStudy";
    case "/case-study/stzy-study":
      return "Pabloo Stzy CaseStudy";

    case "/blogs":
      return "Pabloo Blogs Page";
    case "/blogs/home":
      return "Store Credit Success Stories | Blogs";
    case "/blogs/users-users-users":
      return "Users Users Users — How HQ Trivia Built a Massive User Base | Pabloo";

    case "/thank-you":
      return "Pabloo Purchase";

    case "/features":
      return "Pabloo Features";

    case "/bookCall":
      return "Pabloo Book Your Call";
    case "/scheduleCall":
      return "Pabloo Schedule Call";
    case "/guided":
      return "Pabloo Guided Economy";
    case "/managed":
      return "Pabloo Managed Economy";

    case "/forward*":
      return "Pabloo Store Credit & Cashback";
    case "/cart":
      return "Pabloo Cart";
    case "/checkout":
      return "Pabloo Checkout";
    case "/payment":
      return "Pabloo Complete Payment";
    case "/status":
      return "Pabloo Order Status";
    case "/products":
      return "Pabloo Products";
    case "/products/cashback":
      return "Pabloo Cashback Product";
    case "/products/memberships":
      return "Pabloo Memberships Product";
    case "/products/store-credit-refunds":
      return "Pabloo Store Credit Refunds Product";
    case "/solutions":
      return "Pabloo Solutions";
    case "/solutions/cashback":
      return "Shopify Cashback Solutions with Store Credit";
    case "/solutions/memberships":
      return "Memberships Solutions with Store Credit";
    case "/solutions/store-credit-refunds":
      return "Store Credit Refunds Solutions";

    //   TODO: Create these pages
    case "/solutions/cashback/shopify-cashback-vs-discounts":
      return "Shopify Cashback or Shopify Discounts in 2026 | Pabloo Store Credit & Cashback";
    case "/solutions/cashback/shopify-cashback-examples":
      return "Shopify Cashback Examples in 2026 | Pabloo Store Credit & Cashback";

    default:
      return "Pabloo Store Credit & Cashback";
  }
};

// Component to update page title using Helmet
const PageTitle = () => {
  const location = useLocation();
  const title = getPageTitle(location.pathname);
  return <Helmet><title>{title}</title></Helmet>;
};

// Component to track page views (analytics only)
const TrackPageViews = () => {
  const location = useLocation();
  useEffect(() => {
    const pageTitle = getPageTitle(location.pathname);
    window.gtag("event", "page_view", {
      page_title: pageTitle,
      page_path: location.pathname,
    });
  }, [location]);
  return null;
};

// Scroll to top on every route change
const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
};

const ExternalRedirect = ({ to }) => {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
};

if (process.env.NODE_ENV !== "development") {
  console.log = () => {}
}

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
    <React.StrictMode>
      <HelmetProvider>
        <ClerkProvider
            publishableKey={publishableKey}
        >
          <Router>
            <PageTitle />
            <TrackPageViews />
            <ScrollToTop />
            <Routes>
              <Route path="/" rel="Home" element={<HomePage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/plans" element={<PricingPlansPage />} />
              <Route path="/partners" element={<PartnerPage />} />
              <Route path="/partners/join" element={<PartnersJoin />} />
              <Route path="/store-credit-api" element={<StoreCreditApi />} />
              <Route path="/case-study" element={<CaseStudy />} />

              <Route path="/dashboard" element={<ExternalRedirect to="https://app.pabloo.com/overview" />} />
              <Route path="/dashboard/*" element={<ExternalRedirect to="https://app.pabloo.com/overview" />} />

              <Route path="/scheduleCall" element={<ScheduleCall />} />
              <Route path="/bookCall" element={<BookCallPage />} />
              <Route path="/guided" element={<GuidedEconomy />} />
              <Route path="/managed" element={<ManagedPage />} />

              <Route path="/features" element={<Features />} />
              <Route path="/products" element={<MainProductPage />} />
              <Route path="/products/cashback" element={<CashbackProductPage />} />
              <Route path="/products/memberships" element={<MembershipsProductPage />} />
              <Route path="/products/store-credit-refunds" element={<StoreCreditRefundsProductPage />} />

              {/* Blogs Pages */}
              <Route path="/blogs" element={<BlogsHubPage />} />
              <Route path="/blogs/home" element={<BlogsHubPage />} />
              <Route path="/blogs/users-users-users" element={<UsersUsersUsersPage />} />

              {/* Solution Pages */}
              <Route path="/solutions/cashback" element={<CashbackSolutionsPage />} />
              <Route path="/solutions/memberships" element={<CashbackSolutionsPage />} />
              <Route path="/solutions/store-credit-refunds" element={<CashbackSolutionsPage />} />

              {/* TODO: Update these pages */}
              <Route path="/solutions" element={<MainSolutionsPage />} />
              <Route path="/solutions/shopify/cashback/free-shopify-cashback-app" element={<CashbackInfoPage />} />
              <Route path="/solutions/shopify-cart-abandonment-solutions" element={<CartAbandonmentPage />} />
              <Route path="/solutions/shopify/cashback/shopify-cashback-vs-discounts" element={<CashbackVsDiscountsPage />} />
              <Route path="/solutions/shopify/cashback/shopify-cashback-examples" element={<CashbackSuccessPage />} />
              <Route path="/solutions/shopify/no-more-chargebacks" element={<NoChargebacksComponent />} />
              <Route path="/solutions/shopify/inventory-liquidation" element={<InventoryLiquidation />} />

              {/* Account Pages */}
              <Route path="/manageAccount" element={<CashbackSuccessPage />} />



              <Route path="/forward*" element={<ForwardingPage />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/payment" element={<PaymentPage />} />
              <Route path="/status" element={<OrderStatusPage />} />

              {/* MISC Pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-and-conditions" element={<TermsPage />} />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Router>
        </ClerkProvider>
      </HelmetProvider>
    </React.StrictMode>
);

reportWebVitals();